import { useEffect, useState } from "react";
import { TrashIcon, ClockIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";
import { theme } from "@/lib/theme";
import type { AskSession } from "@/api/client";
import type { Podcast } from "@/hooks/usePodcasts";

interface SessionCardProps {
  session: AskSession;
  podcast: Podcast | null;
  isActive: boolean;
  hasPendingRequest?: boolean;
  onClick: () => void;
  onDelete: () => void;
}

function formatTimeRemaining(expiresAt: string): string {
  const now = Date.now();
  const expiry = new Date(expiresAt).getTime();
  const remaining = expiry - now;

  if (remaining <= 0) return "Expired";

  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function getExpiryStatus(expiresAt: string): "normal" | "warning" | "danger" {
  const now = Date.now();
  const expiry = new Date(expiresAt).getTime();
  const remaining = expiry - now;

  if (remaining <= 0) return "danger";
  if (remaining < 30 * 60 * 1000) return "danger"; // < 30 min
  if (remaining < 60 * 60 * 1000) return "warning"; // < 1 hour
  return "normal";
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export default function SessionCard({
  session,
  podcast,
  isActive,
  hasPendingRequest = false,
  onClick,
  onDelete,
}: SessionCardProps) {
  const [timeRemaining, setTimeRemaining] = useState(formatTimeRemaining(session.expires_at));
  const [expiryStatus, setExpiryStatus] = useState(getExpiryStatus(session.expires_at));

  useEffect(() => {
    const updateTime = () => {
      setTimeRemaining(formatTimeRemaining(session.expires_at));
      setExpiryStatus(getExpiryStatus(session.expires_at));
    };

    // Update every second when danger, every minute otherwise
    const interval = setInterval(updateTime, expiryStatus === "danger" ? 1000 : 60000);
    return () => clearInterval(interval);
  }, [session.expires_at, expiryStatus]);

  const snippet = session.first_question_snippet || "New conversation";
  const messageCount = session.message_count ?? 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "w-full text-left p-3 rounded-lg transition-all group relative cursor-pointer",
        "border-2",
        isActive
          ? "bg-primary-orange/20 dark:bg-primary-orange-dark/20 border-primary-orange dark:border-primary-orange-dark"
          : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500",
      )}
    >
      {/* Podcast info */}
      <div className="flex items-center gap-2 mb-1.5 min-w-0 overflow-hidden">
        <span className="text-lg relative shrink-0">
          {podcast?.emoji ?? "🎧"}
          {/* Pending request indicator */}
          {hasPendingRequest && (
            <span
              className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-primary-orange dark:bg-primary-orange-dark animate-pulse"
              title="Request in progress"
            />
          )}
        </span>
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {podcast?.name ?? session.podcast_slug}
        </span>
      </div>

      {/* Question snippet */}
      <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 mb-2">
        {snippet}
      </p>

      {/* Meta row */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>
          {messageCount} {messageCount === 1 ? "msg" : "msgs"} · {formatRelativeTime(session.last_activity_at)}
        </span>
        <div
          className={cn(
            "flex items-center gap-1 px-1.5 py-0.5 rounded",
            expiryStatus === "danger" && "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400",
            expiryStatus === "warning" && "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400",
            expiryStatus === "normal" && "text-gray-500 dark:text-gray-400",
          )}
        >
          <ClockIcon className="w-3 h-3" />
          <span>{timeRemaining}</span>
        </div>
      </div>

      {/* Delete button - appears on hover */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className={cn(
          "absolute top-2 right-2 p-1.5 rounded-md",
          "opacity-0 group-hover:opacity-100 transition-opacity",
          "bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900/50",
          "text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400",
        )}
        title="Delete session"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </div>
  );
}
